import axios from 'axios';
import { TOKEN_TYPE, getTokenFromCookies } from '@/lib/cookies';

// Base URL for auth service
import { SSO_API_BASE_URL } from './endpoints';

const AUTH_BASE_URL = SSO_API_BASE_URL;

// Create axios instance for Google Drive API
const googleDriveAxios = axios.create({
  baseURL: AUTH_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests
googleDriveAxios.interceptors.request.use((config) => {
  const token = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ExchangeCodeResponse {
  success: boolean;
  data?: {
    access_token: string;
    expires_in: number;
    token_type: string;
  };
  code?: number;
  messageCode?: string;
  message?: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  data?: {
    access_token: string;
    expires_in: number;
    token_type: string;
  };
  code?: number;
  messageCode?: string;
  message?: string;
}

export interface RevokeResponse {
  success: boolean;
  data?: {
    message: string;
  };
  code?: number;
  messageCode?: string;
  message?: string;
}

export interface DriveStatusResponse {
  success: boolean;
  data?: {
    connected: boolean;
    google_email?: string | null;
    email_match?: boolean;
    scope?: string;
    updated_at?: number;
  };
  code?: number;
  messageCode?: string;
  message?: string;
}

export const googleDriveAPI = {
  getStatus: async (): Promise<DriveStatusResponse> => {
    try {
      const response = await googleDriveAxios.get('/v1/auth/google-drive/status');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) return error.response.data;
      throw error;
    }
  },
  /**
   * Exchange authorization code for access token
   * Backend will save refresh token internally
   */
  exchangeCode: async (authorizationCode: string): Promise<ExchangeCodeResponse> => {
    try {
      const response = await googleDriveAxios.post('/v1/auth/google-drive/exchange-code', {
        code: authorizationCode,
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  },

  /**
   * Refresh access token using refresh token stored in backend
   */
  refreshToken: async (): Promise<RefreshTokenResponse> => {
    try {
      const response = await googleDriveAxios.post('/v1/auth/google-drive/refresh-token', {});
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  },

  /**
   * Revoke Google Drive access
   */
  revoke: async (): Promise<RevokeResponse> => {
    try {
      const response = await googleDriveAxios.delete('/v1/auth/google-drive/revoke');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  },
};
